import React, { useContext, useEffect, useState } from 'react';

import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { populate as populatePlan } from '@go-client/lib/api/plan';
import { lastEl } from '@go-shared/lib/functional';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { RouteComponentProps } from 'react-router-dom';
import { FeedEditorCtx } from '@go-client/context/FeedEdtitorContext';
import { ExportProgramParams, FeedEditor, FeedEditorInfo } from './FeedEditor';
import { FeedPage } from '@go-shared/types/feedTemplate';
import {
  createContent,
  updateContent,
  deleteContent,
  exportFeed,
  getDayTemplate,
  updateFeedEditor,
  updateEvent,
  createEvent,
  deleteEvent,
} from '@go-client/lib/api/template';
import { Alert, Anchor } from '@spark-labs/avocado-ui';
import { resolvePlannerPath, resolveTemplatePlannerPath } from '@go-client/routes';
import { BareSimpleSailing, BareSimpleSailingDay } from '@go-shared/types/sailing';
import { BareContent } from '@go-shared/types/content';
import { BareEvent } from '@go-shared/types/event';

interface TemplateFeedEditorParams {
  template: string;
}

type TemplateFeedEditorProps = RouteComponentProps<TemplateFeedEditorParams>;

export default function TemplateFeedEditor(props: TemplateFeedEditorProps) {
  const { activeProperty, refreshContext: refreshActiveProperty } = useActiveProperty();
  const { defaultLanguage, languages, plugins } = useServerInfo();
  const { settingsPanel, setSettingsPanel } = useContext(FeedEditorCtx);
  const { user } = useUserInfo();
  const [editorInfo, setEditorInfo] = useState<FeedEditorInfo | undefined>(undefined);

  const { template: templateId } = props.match.params;

  async function loadEditorInfo(skipTemplates?: boolean) {
    const template = await getDayTemplate(templateId);

    if (!template || !template.feeds) {
      throw new Error(`Could not find template ${templateId}, or template does not have a feed...`);
    }

    let barePlan = template.plan;
    let plan = populatePlan(barePlan, activeProperty);

    const currPlannerLink = resolveTemplatePlannerPath(plan.property._id, template._id);

    setEditorInfo({
      plan,
      groupEvts: [],
      template,
      feeds: template.feeds,
      pathToPlanner: currPlannerLink,
      skipTemplates,
    });
  }

  useEffect(() => {
    loadEditorInfo().catch(console.error);
  }, [templateId]);

  async function handleExportProgram(params: ExportProgramParams, feedSlot: number) {
    if (!editorInfo || !editorInfo.template) return;

    const { template } = editorInfo;

    let links = await exportFeed(
      template._id,
      {
        legacy: false,
        lang: params.language,
        hideBackground: params.hideBackground,
      },
      feedSlot
    );

    await Alert.success({
      htmlContent: (
        <div className="pt-2 pb-4">
          {links.map((lnk, i) => (
            <div>
              <Anchor target="_blank" href={lnk}>
                {/* FIXME: hack, assumes pdf is first and htmls follow */}
                Download {lastEl(lnk.split('.')).toUpperCase()} {i !== 0 ? `${i}` : ''}
              </Anchor>
            </div>
          ))}
        </div>
      ),
    });
  }

  async function handleUpdateProgram(currentPage: FeedPage, feedSlot: number) {
    if (!editorInfo || !editorInfo.template) return;

    await updateFeedEditor(activeProperty, editorInfo.template._id, currentPage, feedSlot);
  }

  function handleOnSailingDayNameClick(
    sailing: BareSimpleSailing,
    sailingDay: BareSimpleSailingDay
  ) {
    props.history.push(resolvePlannerPath(activeProperty._id, sailing._id, sailingDay._id));
  }

  const showDailyProgramFeedOptions = !!plugins.find(p => p.name === 'ddp');

  function handleOnCreateContent(content: BareContent) {
    return createContent(templateId, content);
  }

  function handleOnUpdateContent(content: BareContent) {
    return updateContent(templateId, content);
  }

  function handleOnDeleteContent(contentId: string) {
    return deleteContent(templateId, contentId);
  }

  async function handleOnUpdateEvent(event: BareEvent, applyStaffToPlan?: boolean) {
    if (editorInfo) {
      return updateEvent(templateId, event, applyStaffToPlan);
    }
  }

  async function handleOnCreateEvent(event: BareEvent, applyStaffToPlan?: boolean) {
    if (editorInfo) {
      return createEvent(templateId, event, applyStaffToPlan);
    }
  }

  async function handleOnDeleteEvent(eventId: string) {
    if (editorInfo) {
      return deleteEvent(templateId, eventId);
    }
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <FeedEditor
        {...props}
        variant="velvet"
        user={user}
        property={activeProperty}
        refreshActiveProperty={refreshActiveProperty}
        defaultLanguage={defaultLanguage}
        languages={languages}
        showDailyProgramFeedOptions={showDailyProgramFeedOptions}
        settingsPanel={settingsPanel}
        setSettingsPanel={setSettingsPanel}
        editorInfo={editorInfo}
        needsRefresh={loadEditorInfo}
        onExportProgram={handleExportProgram}
        onUpdateProgram={handleUpdateProgram}
        onSailingDayNameClick={handleOnSailingDayNameClick}
        onCreateContent={handleOnCreateContent}
        onUpdateContent={handleOnUpdateContent}
        onDeleteContent={handleOnDeleteContent}
        onUpdateEvent={handleOnUpdateEvent}
        onCreateEvent={handleOnCreateEvent}
        onDeleteEvent={handleOnDeleteEvent}
      />
    </DndProvider>
  );
}
